import { formatPhoneNumber, PhoneRegion } from '@/helpers/phoneHelper';
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import GBFlag from 'country-flag-icons/react/3x2/GB';
import IEFlag from 'country-flag-icons/react/3x2/IE';
import JPFlag from 'country-flag-icons/react/3x2/JP';
import { FC, Fragment } from 'react';
import { FiChevronDown, FiGlobe } from 'react-icons/fi';
import { merge } from '../../helpers/Utility';
import strings from '../../pages/team/team.translations';
import { withField } from './withField';

export interface PhoneNumberValue {
  number: string;
  region: PhoneRegion;
}

interface PhoneRegionDisplayOption {
  dialCode: string;
  id: PhoneRegion;
  name: string;
}

interface InjectedProps {
  value: PhoneNumberValue;
  onChange: (v: PhoneNumberValue) => void;
}

interface PhoneNumberInputProps {
  className?: string;
  customValidatorMessage?: string;
  disabled?: boolean;
  name: string;
  validator?: (value: PhoneNumberValue) => boolean;
}

const phoneRegionOptions: PhoneRegionDisplayOption[] = [
  { id: PhoneRegion.Japan, name: strings.addTeamMemberForm.phoneRegionJapan, dialCode: '+81' },
  { id: PhoneRegion.UnitedKingdom, name: strings.addTeamMemberForm.phoneRegionUnitedKingdom, dialCode: '+44' },
  { id: PhoneRegion.Ireland, name: strings.addTeamMemberForm.phoneRegionIreland, dialCode: '+353' },
  { id: PhoneRegion.Other, name: strings.addTeamMemberForm.phoneRegionOther, dialCode: '' }
];

const phonePlaceholderByRegion: Record<PhoneRegion, string> = {
  [PhoneRegion.Japan]: '09012345678',
  [PhoneRegion.UnitedKingdom]: '07700900000',
  [PhoneRegion.Ireland]: '0871234567',
  [PhoneRegion.Other]: '+441234567890'
};

const DEFAULT_VALUE: PhoneNumberValue = {
  number: '',
  region: PhoneRegion.Other
};

const FlagIcon: FC<{ region: PhoneRegion }> = ({ region }) => {
  switch (region) {
    case PhoneRegion.Japan:
      return <JPFlag title="Japan" className="h-5 w-7 rounded-[2px] border border-black/10 shadow-sm shrink-0" />;
    case PhoneRegion.UnitedKingdom:
      return (
        <GBFlag title="United Kingdom" className="h-5 w-7 rounded-[2px] border border-black/10 shadow-sm shrink-0" />
      );
    case PhoneRegion.Ireland:
      return <IEFlag title="Ireland" className="h-5 w-7 rounded-[2px] border border-black/10 shadow-sm shrink-0" />;
    default:
      return <FiGlobe className="h-5 w-5 text-gray-500 shrink-0" />;
  }
};

export const PhoneNumberInput: FC<PhoneNumberInputProps & InjectedProps> = ({
  className,
  disabled,
  name,
  onChange,
  value = DEFAULT_VALUE
}) => {
  const selectedRegion = phoneRegionOptions.find(o => o.id === value.region) ?? phoneRegionOptions[3];

  const handleRegionSelect = (option: PhoneRegionDisplayOption) => {
    onChange({ ...value, region: option.id });
  };

  const handleNumberChange = (raw: string) => {
    const formatted = formatPhoneNumber(raw);
    onChange({ ...value, number: formatted ?? '' });
  };

  return (
    <div className={merge('flex rounded-md border border-primary-std text-primary-std gap-1', className)}>
      <Menu as="div" className="relative border-r">
        {({ open }) => (
          <>
            <MenuButton
              as="button"
              type="button"
              disabled={disabled}
              className={merge(
                'border-primary-std bg-primary-std focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
                'flex w-16 items-center justify-between rounded-md py-2 pl-2 pr-2 focus:outline-none sm:text-sm shrink-0',
                disabled ? 'cursor-not-allowed opacity-70' : ''
              )}
            >
              <FlagIcon region={selectedRegion.id} />
              <FiChevronDown className="text-secondary-std" />
            </MenuButton>

            <Transition
              as={Fragment}
              show={open}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <MenuItems
                anchor="bottom start"
                className="overflow-hidden rounded-md border bg-primary-std shadow-lg focus:outline-none z-[9999]"
              >
                {phoneRegionOptions.map(option => (
                  <MenuItem key={option.id}>
                    {({ active }) => (
                      <button
                        type="button"
                        className={merge(
                          'flex w-full items-center gap-3 px-3 py-2 text-left sm:text-sm',
                          active ? 'bg-secondary-std' : ''
                        )}
                        onClick={() => handleRegionSelect(option)}
                      >
                        <FlagIcon region={option.id} />
                        <span className="flex-1 whitespace-nowrap">{option.name}</span>
                        {option.dialCode && (
                          <span className="text-secondary-std whitespace-nowrap">{option.dialCode}</span>
                        )}
                      </button>
                    )}
                  </MenuItem>
                ))}
              </MenuItems>
            </Transition>
          </>
        )}
      </Menu>

      <input
        disabled={disabled}
        id={name}
        name={name}
        className="pl-2 focus:outline-none focus:ring-2 focus:ring-primary-500 bg-primary-std focus:border-primary-500 flex-1 min-w-0 block w-full sm:text-sm border-primary-std rounded-md"
        placeholder={phonePlaceholderByRegion[value.region]}
        value={value.number}
        onChange={e => handleNumberChange(e.target.value)}
      />
    </div>
  );
};

export const PhoneNumberField = withField<PhoneNumberValue, PhoneNumberInputProps>(PhoneNumberInput);
